import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, tap } from "rxjs";
import { translate } from '@jsverse/transloco';
import { AppNotificationService } from "src/app/shared/services/notification.service";
import { ProductService } from "src/app/products/services/product.service";
import * as ProductsActions from "./actions";
import { IProduct } from "src/app/products/models/product";

@Injectable()
export class ProductsEffets {

    ///////////////////
    // GET PRODUCTS //
    /////////////////

    // Get Products
    getProducts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductsActions.getProducts), 
            mergeMap((data) => {
            return this.productService.listProducts({keyword: data.request.keyword, pageNumber: data.request.pageNumber, pageSize: data.request.pageSize}).pipe(
                map((response) => 
                    ProductsActions.getProductsSuccess({ products: response.data, total: response.totalCount })
                ), 
                catchError((error) =>
                    of(ProductsActions.getProductsFailure({ error: error.message }))
                )
            );
        })
        )
    );




    //////////////////////
    // CREATE PRODUCT  //
    ////////////////////

    // Create Product
    createProduct$ = createEffect(() =>
      this.actions$.pipe(
          ofType(ProductsActions.createProduct),
          mergeMap((action) => 
              this.productService.addProduct(action.product).pipe(
                  map((product: IProduct) => ProductsActions.createProductSuccess({ product })),
                  catchError((error) => of(ProductsActions.createProductFailure({ error: error.message })))
              )
          )
      )
    );

    // Product created successfully
    createProductSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(ProductsActions.createProductSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('products.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Product not created successfully
    createProductFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(ProductsActions.createProductFailure)
      ), { dispatch: false }
    );




    ///////////////////////
    // IMPORT PRODUCTS  //
    /////////////////////

    // Import Products 
    importProducts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductsActions.importProducts),
            mergeMap((action) => 
                this.productService.importProducts(action.csv).pipe(
                    map((products: Array<IProduct>) => ProductsActions.importProductsSuccess({ products })),
                    catchError((error) => of(ProductsActions.importProductsFailure({ error: error.message })))
                )
            )
        )
      );
  
      // Products imported successfully
      importProductsSuccess$ = createEffect(() => 
          this.actions$.pipe(
              ofType(ProductsActions.importProductsSuccess),
              tap((products) => {
                this.appnotificationService.notification$.next({ message:translate('products.importSuccess'), style: 'success' });
              })
          ), { dispatch: false }
      );
  
      // Products not imported successfully
      importProductsFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(ProductsActions.importProductsFailure),
            tap((error) => {
                this.appnotificationService.notification$.next({ message:translate('products.importFailed'), style: 'error' });
            })
        ), { dispatch: false }
      );    




    //////////////////////
    // UPDATE PRODUCT  //
    ////////////////////

    // Update Products
    updateProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductsActions.updateProduct),
            mergeMap((action) => 
                this.productService.updateProduct(action.product).pipe(
                    map((product: IProduct) => ProductsActions.updateProductSuccess({ product })),
                    catchError((error) => of(ProductsActions.updateProductFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Products successful
    updateProductSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(ProductsActions.updateProductSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('products.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Products not updated successfully
    updateProductFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(ProductsActions.updateProductFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('products.updateFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );




    //////////////////////
    // DELETE Product  //
    ////////////////////

    // Delete Product
    deleteProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductsActions.deleteProduct), 
            mergeMap((action) => {
                return this.productService.deleteProduct(action.id).pipe(
                    map(() => ProductsActions.deleteProductSuccess({id: action.id})), 
                    catchError((error) => of(ProductsActions.deleteProductFailure({ error: error.message })))
            )}
        ))
    );

    // Product deleted successfully
    deleteProductSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(ProductsActions.deleteProductSuccess),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('products.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Product not deleted successfully
    deleteProductFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProductsActions.deleteProductFailure)
      ), { dispatch: false }
    );




    constructor(
        private actions$: Actions, 
        private productService: ProductService,
        private appnotificationService: AppNotificationService) {}
}